import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import type { EntityDocumentEntityType } from '../types';
import { storage } from './firebase';

const ACCEPTED_EXTENSIONS = ['pdf', 'csv', 'txt', 'xls', 'xlsx', 'docx', 'md', 'json', 'tsv'];

export const ENTITY_DOCUMENT_ACCEPT = ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`).join(',');

export function entityDocumentStoragePath(
  userId: string,
  entityType: EntityDocumentEntityType,
  entityId: string,
  documentId: string,
  fileName: string
): string {
  const ext = fileName.includes('.') ? fileName.slice(fileName.lastIndexOf('.')) : '';
  return `entityDocuments/${userId}/${entityType}/${entityId}/${documentId}${ext}`;
}

export async function uploadEntityDocumentFile(
  userId: string,
  entityType: EntityDocumentEntityType,
  entityId: string,
  documentId: string,
  file: File
): Promise<{ storagePath: string; downloadUrl: string }> {
  const storagePath = entityDocumentStoragePath(userId, entityType, entityId, documentId, file.name);
  const r = ref(storage, storagePath);
  await uploadBytes(r, file, { contentType: file.type || 'application/octet-stream' });
  const downloadUrl = await getDownloadURL(r);
  return { storagePath, downloadUrl };
}

export async function deleteEntityDocumentFile(storagePath: string | undefined): Promise<void> {
  if (!storagePath?.trim()) return;
  try {
    await deleteObject(ref(storage, storagePath.trim()));
  } catch {
    // File may already be gone.
  }
}

export function isAcceptedEntityDocumentFile(file: File): boolean {
  const name = file.name.trim().toLowerCase();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';
  return ACCEPTED_EXTENSIONS.includes(ext);
}

export function entityDocumentFileError(file: File): string | null {
  if (file.size > 15 * 1024 * 1024) {
    return 'File exceeds the 15 MB limit.';
  }
  if (!isAcceptedEntityDocumentFile(file)) {
    return 'Unsupported file type. Use PDF, CSV, TXT, XLS, XLSX, or DOCX.';
  }
  return null;
}
